// frontend/src/components/planner/ReportsPanel.jsx
// Reports Panel — Spending by Category, Month, Account, and Income vs Expenses

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { Download, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import AppShell from '../layout/AppShell';
import LoadingSpinner from '../common/LoadingSpinner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) => {
  const num = parseFloat(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

const amountClass = (n) => {
  const num = parseFloat(n) || 0;
  if (num > 0) return 'text-emerald-600 font-semibold';
  if (num < 0) return 'text-red-600 font-semibold';
  return 'text-gray-700 font-semibold';
};

const formatDate = (d) => format(d, 'yyyy-MM-dd');

// ---------------------------------------------------------------------------
// Report tab definitions
// ---------------------------------------------------------------------------

const REPORT_TABS = [
  { id: 'spending-by-category', label: 'By Category', dateMode: 'range' },
  { id: 'spending-by-month', label: 'By Month', dateMode: 'months' },
  { id: 'spending-by-account', label: 'By Account', dateMode: 'range' },
  { id: 'income-vs-expenses', label: 'Income vs Expenses', dateMode: 'months' },
];

const MONTH_OPTIONS = [3, 6, 12];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ReportsPanel() {
  // --- State ---------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('spending-by-category');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState(() => formatDate(subDays(new Date(), 30)));
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [months, setMonths] = useState(6);
  const [bankAccountId, setBankAccountId] = useState('');

  // Accounts list for filter dropdown
  const [accountsList, setAccountsList] = useState([]);

  const tabDef = useMemo(() => REPORT_TABS.find((t) => t.id === activeTab), [activeTab]);

  // --- Load accounts on mount ---------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function loadAccounts() {
      try {
        const res = await api.get('/api/planner/accounts');
        if (!cancelled && res.data.accounts) {
          setAccountsList(res.data.accounts);
        }
      } catch {
        // Non-critical — dropdown just won't populate
      }
    }
    loadAccounts();
    return () => { cancelled = true; };
  }, []);

  // --- Build query params --------------------------------------------------
  const buildParams = useCallback(() => {
    const params = {};
    if (tabDef.dateMode === 'range') {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.months = months;
    }
    // Account filter available on all tabs except "by-account" (which aggregates across accounts)
    if (bankAccountId && activeTab !== 'spending-by-account') {
      params.bank_account_id = bankAccountId;
    }
    return params;
  }, [tabDef, startDate, endDate, months, bankAccountId, activeTab]);

  // --- Fetch report data ---------------------------------------------------
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setData([]);
    try {
      const params = buildParams();
      const res = await api.get(`/api/reports/${activeTab}`, { params });
      if (res.data.success !== false) {
        // API returns { data: [...] } or { summary: [...] } depending on report
        const rows = res.data.data || res.data.summary || res.data.rows || (Array.isArray(res.data) ? res.data : []);
        setData(rows);
      } else {
        setError(res.data.error || 'Failed to load report');
      }
    } catch (err) {
      console.error('Report fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, buildParams]);

  // Re-fetch whenever tab or filters change
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // --- Export CSV -----------------------------------------------------------
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { report_type: activeTab.replace(/-/g, '_'), ...buildParams() };
      const res = await api.get('/api/reports/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}-${formatDate(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };



  // --- Determine if the account filter should show -------------------------
  const showAccountFilter = activeTab !== 'spending-by-account';

  // =========================================================================
  // Column definitions per report
  // =========================================================================
  const columns = useMemo(() => {
    switch (activeTab) {
      case 'spending-by-category':
        return [
          { key: 'category', label: 'Category', align: 'left' },
          { key: 'transaction_count', label: 'Transactions', align: 'right' },
          { key: 'total_spent', label: 'Total Spent', align: 'right', isCurrency: true, colorMode: 'expense' },
          { key: 'avg_expense', label: 'Avg Expense', align: 'right', isCurrency: true, colorMode: 'expense' },
        ];
      case 'spending-by-month':
        return [
          { key: 'month', label: 'Month', align: 'left' },
          { key: 'transaction_count', label: 'Transactions', align: 'right' },
          { key: 'total_expenses', label: 'Expenses', align: 'right', isCurrency: true, colorMode: 'expense' },
          { key: 'total_income', label: 'Income', align: 'right', isCurrency: true, colorMode: 'income' },
          { key: 'net_flow', label: 'Net', align: 'right', isCurrency: true, colorMode: 'auto' },
        ];
      case 'spending-by-account':
        return [
          { key: 'account_name', label: 'Account', align: 'left' },
          { key: 'institution_name', label: 'Institution', align: 'left' },
          { key: 'transaction_count', label: 'Transactions', align: 'right' },
          { key: 'total_expenses', label: 'Expenses', align: 'right', isCurrency: true, colorMode: 'expense' },
          { key: 'total_income', label: 'Income', align: 'right', isCurrency: true, colorMode: 'income' },
          { key: 'current_balance', label: 'Balance', align: 'right', isCurrency: true, colorMode: 'auto' },
        ];
      case 'income-vs-expenses':
        return [
          { key: 'month', label: 'Month', align: 'left' },
          { key: 'income', label: 'Income', align: 'right', isCurrency: true, colorMode: 'income' },
          { key: 'expenses', label: 'Expenses', align: 'right', isCurrency: true, colorMode: 'expense' },
          { key: 'savings', label: 'Savings', align: 'right', isCurrency: true, colorMode: 'auto' },
          { key: 'savings_rate', label: 'Savings Rate', align: 'right', isPercent: true, computed: true },
        ];
      default:
        return [];
    }
  }, [activeTab]);

  // --- Cell renderer -------------------------------------------------------
  const renderCell = (row, col) => {
    // Compute savings_rate on the fly: savings / income * 100
    let raw = row[col.key];
    if (col.computed && col.key === 'savings_rate') {
      const inc = parseFloat(row.income) || 0;
      raw = inc > 0 ? ((parseFloat(row.savings) || 0) / inc * 100) : 0;
    }

    if (col.isPercent) {
      const pct = parseFloat(raw) || 0;
      return (
        <span className={pct >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
          {pct.toFixed(1)}%
        </span>
      );
    }

    if (col.isCurrency) {
      const num = parseFloat(raw) || 0;
      let cls = 'text-gray-700 font-semibold';
      if (col.colorMode === 'expense') cls = 'text-red-600 font-semibold';
      else if (col.colorMode === 'income') cls = 'text-emerald-600 font-semibold';
      else if (col.colorMode === 'auto') cls = amountClass(num);
      return <span className={cls}>{fmt(num)}</span>;
    }

    // Plain text
    return <span className="text-gray-900">{raw ?? '--'}</span>;
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* ----------------------------------------------------------------
            Header
        ---------------------------------------------------------------- */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Reports</h1>
            <p className="text-base sm:text-sm text-gray-600 mt-1">
              Analyze your spending patterns and income trends
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || loading || data.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export CSV
          </button>
        </div>

        {/* ----------------------------------------------------------------
            Card — Tabs + Filters + Table
        ---------------------------------------------------------------- */}
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">

          {/* Tab Bar */}
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px" aria-label="Report tabs">
              {REPORT_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-sm font-medium border-b-2 transition-colors min-h-[44px] sm:min-h-0 ${
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Filters Row */}
          <div className="border-b border-gray-100 bg-gray-50/60 px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
              {/* Date controls — range mode */}
              {tabDef.dateMode === 'range' && (
                <>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 min-h-[44px] sm:min-h-0"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 min-h-[44px] sm:min-h-0"
                    />
                  </div>
                </>
              )}

              {/* Date controls — month count mode */}
              {tabDef.dateMode === 'months' && (
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Time Period</label>
                  <div className="flex gap-2">
                    {MONTH_OPTIONS.map((m) => (
                      <button
                        key={m}
                        onClick={() => setMonths(m)}
                        className={`px-4 py-2 rounded-lg text-base sm:text-sm font-medium transition-colors min-h-[44px] sm:min-h-0 ${
                          months === m
                            ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300'
                            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {m}mo
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Account filter */}
              {showAccountFilter && (
                <div className="flex-1 min-w-0 sm:max-w-[220px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Account</label>
                  <select
                    value={bankAccountId}
                    onChange={(e) => setBankAccountId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 min-h-[44px] sm:min-h-0 bg-white"
                  >
                    <option value="">All Accounts</option>
                    {accountsList.map((acct) => (
                      <option key={acct.id} value={acct.id}>
                        {acct.name || acct.official_name || `Account ${acct.mask || acct.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* ---------------------------------------------------------------
              Body — Loading / Error / Empty / Table
          --------------------------------------------------------------- */}
          <div className="min-h-[300px]">
            {loading && (
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="lg" text="Loading report..." />
              </div>
            )}

            {!loading && error && (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-base sm:text-sm text-red-600 font-medium">{error}</p>
                <button
                  onClick={fetchReport}
                  className="mt-4 px-4 py-2 text-base sm:text-sm text-emerald-600 hover:text-emerald-700 font-medium underline min-h-[44px] sm:min-h-0"
                >
                  Try Again
                </button>
              </div>
            )}

            {!loading && !error && data.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-base sm:text-sm text-gray-500 font-medium">No data for the selected filters</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting the date range or account filter.</p>
              </div>
            )}

            {!loading && !error && data.length > 0 && (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        {columns.map((col) => (
                          <th
                            key={col.key}
                            className={`px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider ${
                              col.align === 'right' ? 'text-right' : 'text-left'
                            }`}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.map((row, idx) => (
                        <tr
                          key={idx}
                          className={`transition-colors hover:bg-emerald-50/40 ${
                            idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                          }`}
                        >
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                            >
                              {renderCell(row, col)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden divide-y divide-gray-100">
                  {data.map((row, idx) => (
                    <div
                      key={idx}
                      className={`px-4 py-4 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                    >
                      {/* Primary identifier — first column rendered as a title */}
                      <div className="font-semibold text-gray-900 text-base mb-2">
                        {row[columns[0].key] ?? '--'}
                      </div>

                      {/* Remaining columns as label-value pairs */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {columns.slice(1).map((col) => (
                          <div key={col.key} className="flex flex-col">
                            <span className="text-xs text-gray-500">{col.label}</span>
                            <span className="text-sm">{renderCell(row, col)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer — row count */}
          {!loading && data.length > 0 && (
            <div className="border-t border-gray-100 bg-gray-50/60 px-4 sm:px-6 py-2.5">
              <p className="text-xs text-gray-500">
                {data.length} {data.length === 1 ? 'row' : 'rows'}
              </p>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
